package com.vamvisions.main.layout

import com.vamvisions.main.theme.NARROW_SCREEN
import com.vamvisions.main.theme.Tokens
import com.vamvisions.main.theme.cssVar
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.media

object LayoutStyles : StyleSheet() {

    val page by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("min-height", "100vh")
    }

    val skipLink by style {
        property("position", "absolute")
        property("left", cssVar(Tokens.SPACE_3))
        property("top", "-10rem")
        property("padding", "${cssVar(Tokens.SPACE_2)} ${cssVar(Tokens.SPACE_3)}")
        property("background-color", cssVar(Tokens.COLOR_SURFACE))
        property("border-radius", cssVar(Tokens.RADIUS))
        property("z-index", "10")
        self + ":focus" style {
            property("top", cssVar(Tokens.SPACE_2))
        }
    }

    val header by style {
        property("background-color", cssVar(Tokens.COLOR_SURFACE_TRANSLUCENT))
    }

    /** Anchors the narrow-screen [navList] dropdown, so it opens under the whole header, inside the viewport. */
    val headerRow by style {
        property("position", "relative")
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "${cssVar(Tokens.SPACE_2)} ${cssVar(Tokens.SPACE_3)}")
        property("padding-block", cssVar(Tokens.SPACE_2))
    }

    val brand by style {
        property("font-weight", "700")
        property("text-decoration", "none")
        property("color", cssVar(Tokens.COLOR_BRAND))
    }

    val nav by style {
        property("position", "relative")
        media(NARROW_SCREEN) {
            self style {
                property("position", "static")
            }
        }
    }

    /** Only shown on narrow screens, where it opens and closes [navList]. */
    val menuButton by style {
        property("display", "none")
        media(NARROW_SCREEN) {
            self style {
                property("display", "inline-block")
            }
        }
    }

    /**
     * A row of links on wide screens. On narrow screens, a dropdown under the header, at the page gutter: closed, it
     * is `visibility: hidden`, so its links are out of the tab order.
     */
    val navList by style {
        property("display", "flex")
        property("gap", cssVar(Tokens.SPACE_3))
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        media(NARROW_SCREEN) {
            self style {
                property("position", "absolute")
                property("top", "calc(100% + ${cssVar(Tokens.SPACE_1)})")
                property("left", cssVar(Tokens.SPACE_3))
                property("z-index", "10")
                property("flex-direction", "column")
                property("gap", cssVar(Tokens.SPACE_1))
                property("min-width", "12rem")
                property("padding", cssVar(Tokens.SPACE_2))
                property("background-color", cssVar(Tokens.COLOR_SURFACE))
                property("border-radius", cssVar(Tokens.RADIUS))
                property("box-shadow", cssVar(Tokens.SHADOW_CARD))
                property("visibility", "hidden")
                property("opacity", "0")
                property("transform", "translateY(-0.5rem)")
                property("transition", "transform 0.2s ease-out, opacity 0.2s ease-out, visibility 0.2s")
            }
        }
        media("(prefers-reduced-motion: reduce)") {
            self style {
                property("transition", "none")
            }
        }
    }

    val navListOpen by style {
        media(NARROW_SCREEN) {
            self style {
                property("visibility", "visible")
                property("opacity", "1")
                property("transform", "none")
            }
        }
    }

    val navLink by style {
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("text-decoration", "none")
        media(NARROW_SCREEN) {
            self style {
                property("display", "block")
                property("padding", cssVar(Tokens.SPACE_2))
            }
        }
        self + ":hover" style {
            property("color", cssVar(Tokens.COLOR_BRAND))
        }
        self + "[aria-current=page]" style {
            property("color", cssVar(Tokens.COLOR_BRAND))
            property("font-weight", "600")
            property("text-decoration", "underline")
            property("text-underline-offset", "0.3em")
        }
    }

    val toggles by style {
        property("display", "flex")
        property("gap", cssVar(Tokens.SPACE_2))
        property("margin-left", "auto")
    }

    val toggleButton by style {
        property("min-width", "2.75rem")
        property("min-height", "2.75rem")
        property("padding", "0 ${cssVar(Tokens.SPACE_2)}")
        property("font", "inherit")
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("background", "transparent")
        property("border", "1px solid ${cssVar(Tokens.COLOR_MUTED)}")
        property("border-radius", cssVar(Tokens.RADIUS))
        property("cursor", "pointer")
        self + ":hover" style {
            property("border-color", cssVar(Tokens.COLOR_BRAND))
            property("color", cssVar(Tokens.COLOR_BRAND))
        }
    }

    /** Anchors [languageList] under the language button. */
    val languageMenu by style {
        property("position", "relative")
    }

    val chevron by style {
        property("display", "inline-block")
        property("width", "1em")
        property("height", "1em")
        property("margin-left", cssVar(Tokens.SPACE_1))
        property("vertical-align", "-0.15em")
        property("transition", "transform 0.2s ease-out")
        media("(prefers-reduced-motion: reduce)") {
            self style {
                property("transition", "none")
            }
        }
    }

    val chevronOpen by style {
        property("transform", "rotate(180deg)")
    }

    /**
     * A dropdown under the language button, right-aligned with it so it stays inside the viewport. Closed, it is
     * `visibility: hidden`, so its buttons are out of the tab order.
     */
    val languageList by style {
        property("position", "absolute")
        property("top", "calc(100% + ${cssVar(Tokens.SPACE_1)})")
        property("right", "0")
        property("z-index", "10")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", cssVar(Tokens.SPACE_1))
        property("min-width", "10rem")
        property("margin", "0")
        property("padding", cssVar(Tokens.SPACE_2))
        property("list-style", "none")
        property("background-color", cssVar(Tokens.COLOR_SURFACE))
        property("border-radius", cssVar(Tokens.RADIUS))
        property("box-shadow", cssVar(Tokens.SHADOW_CARD))
        property("visibility", "hidden")
        property("opacity", "0")
        property("transform", "translateY(-0.5rem)")
        property("transition", "transform 0.2s ease-out, opacity 0.2s ease-out, visibility 0.2s")
        media("(prefers-reduced-motion: reduce)") {
            self style {
                property("transition", "none")
            }
        }
    }

    val languageListOpen by style {
        property("visibility", "visible")
        property("opacity", "1")
        property("transform", "none")
    }

    val languageOption by style {
        property("display", "block")
        property("width", "100%")
        property("min-height", "2.75rem")
        property("padding", "0 ${cssVar(Tokens.SPACE_2)}")
        property("font", "inherit")
        property("text-align", "start")
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("background", "transparent")
        property("border", "none")
        property("border-radius", cssVar(Tokens.RADIUS))
        property("cursor", "pointer")
        self + ":hover" style {
            property("color", cssVar(Tokens.COLOR_BRAND))
        }
        self + "[aria-current=true]" style {
            property("color", cssVar(Tokens.COLOR_BRAND))
            property("font-weight", "600")
        }
    }

    /** A translucent panel over the intro background, like the header and footer. */
    val main by style {
        property("flex", "1")
        property("padding-block", cssVar(Tokens.SPACE_4))
        property("background-color", cssVar(Tokens.COLOR_SURFACE_TRANSLUCENT))
        media("(min-width: 62rem)") {
            self style {
                property("margin-block", cssVar(Tokens.SPACE_3))
                property("border-radius", "1rem")
            }
        }
        self + ":focus" style {
            property("outline", "none")
        }
    }

    /** The main area without the panel: the content sits on glass cards over the intro background. */
    val mainBare by style {
        property("flex", "1")
        property("padding-block", "2.67rem ${cssVar(Tokens.SPACE_4)}")
        self + ":focus" style {
            property("outline", "none")
        }
    }

    /**
     * Raises the main panel's width cap for pages laid out in columns, and centres their content vertically when the
     * panel is taller than it.
     */
    val mainWide by style {
        property(Tokens.CONTAINER_MAX_WIDTH, "75rem")
        property("display", "flex")
        property("flex-direction", "column")
        property("justify-content", "center")
    }

    val footer by style {
        property("padding-block", cssVar(Tokens.SPACE_3))
        property("color", cssVar(Tokens.COLOR_MUTED))
        property("font-size", "0.85rem")
        property("background-color", cssVar(Tokens.COLOR_SURFACE_TRANSLUCENT))
    }
}
